import { Button } from "@/components/Button";
import ReactCalendar, { CalendarProps } from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { FaArrowAltCircleLeft, FaArrowAltCircleRight } from "react-icons/fa";
import {
    LuCalendarCheck,
    LuDoorClosed
} from "react-icons/lu";
import styled from "styled-components";

export const toTitleString = (rawString: string) => {
    return rawString[0].toUpperCase() + rawString.slice(1);
};

const formatShortWeekday = (_: unknown, date: Date): string => {
    const fullWeekdays = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sab"];
    return fullWeekdays[date.getDay()];
};

const formatMonthYear = (locale: Intl.LocalesArgument, date: Date) => {
    const month = date.toLocaleString(locale, { month: "long" });
    const year = date.getFullYear();
    return `${toTitleString(month)} ${year}`;
};

const CalendarBase = styled(ReactCalendar)`
    /* display: flex; */
    position: relative;
    width: 27rem;
    height: fit-content;
    display: flex;
    justify-content: center;
    border: none;
    border-radius: 0.75rem;
    /* margin-top: 1.25rem; */

    &.column {
        .react-calendar__viewContainer {
            padding-top: 0;
        }
    }

    * {
        font-family: "Poppins";
    }

    .react-calendar__navigation {
        background-color: #ffffff;
        box-shadow: 0px 2px 5px 3px #00000036;
        border-radius: 1.5rem;
        display: flex;
        height: 3rem;
        min-width: 14rem;

        .react-calendar__navigation__prev-button,
        .react-calendar__navigation__next-button,
        .react-calendar__navigation__label {
            background: none !important;
            color: black;
            font-size: 1rem;
            font-weight: 500;
            display: flex;
            justify-content: center;
            align-items: center;
        }

        .react-calendar__navigation__label {
            pointer-events: none;
        }

        .react-calendar__navigation__next-button,
        .react-calendar__navigation__prev-button {
            transition: 0.2s;
            &:hover {
                transform: scale(1.2);
            }

            &:active {
                transform: scale(0.9);
            }
        }

        .react-calendar__navigation__arrow {
        }
    }

    .react-calendar__month-view__weekdays__weekday {
        abbr {
            text-decoration: none;
            text-transform: none;
            color: #6a6a6a;
            font-weight: 700;
        }
    }

    .react-calendar__viewContainer {
        padding-top: 2rem;
    }

    .react-calendar__month-view {
        flex: 1;
        height: 100%;
    }

    .react-calendar__navigation__next2-button,
    .react-calendar__navigation__prev2-button {
        display: none;
    }

    .react-calendar__tile {
        position: relative;
        height: 3rem;
        width: 2rem;
        border-radius: 0.5rem;
        font-weight: 600;
        color: #6a6a6a;
        font-size: 1rem;
        flex-basis: calc((100% / 7)- 0.1rem) !important;

        &.react-calendar__tile--active {
            background-color: #22b82d;
            color: white;
        }

        &.react-calendar__tile.purple {
            background-color: var(--magenta);
        }

        &.react-calendar__tile.green {
            background-color: #50af4a;
        }

         &.react-calendar__tile.yellow {
            &, * {
                color: #4b4900;
            }
            background-color: var(--yellow);

            &.react-calendar__tile--active * {
                color: white !important;
                /* background-color: red !important; */
            }
        }

        transition: background 0.05s;

        &.disabled {
            transition: none !important;
        }

        /* aspect-ratio: 1/1; */
    }

    .react-calendar__month-view__days__day--neighboringMonth {
        visibility: hidden;
    }
`;

export const Calendar = (props: Partial<CalendarProps>) => {
    return (
        <CalendarBase
            locale="pt"
            minDetail="month"
            // minDate={value}
            selectRange={false}
            formatShortWeekday={formatShortWeekday}
            nextLabel={<FaArrowAltCircleRight />}
            prevLabel={<FaArrowAltCircleLeft />}
            formatMonthYear={formatMonthYear}
            defaultView="month"
            calendarType="hebrew"
            onClickWeekNumber={(weekNumber, date) => {
                // É importante deixar essa função aqui por enquanto
                alert("Week cliclada " + weekNumber + date);
            }}
            {...props}
            // tileContent={({ activeStartDate, date, view }) => (
            //     <p className="price">{priceFormatter.format(129)}</p>
            // )}
        ></CalendarBase>
    );
};

const CalendarWrapper = styled.div`
    display: flex;
    flex-direction: column;
    border-radius: 1rem;
    background-color: #f3f3f3;
    padding: 2.5rem 2.5rem;
    gap: 2.5rem;
    align-items: center;

    * {
        font-family: "Poppins";
    }
`;

const TagsContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    width: 100%;

    > button {
        flex: 1 1 calc(50% - 0.25rem);
        min-width: calc(50% - 0.25rem);
        padding: 0.45rem 0.5rem;
        pointer-events: none;
        font-weight: 700;
        gap: 0.35rem;
        display: flex;
        align-items: center;
        justify-content: flex-start;

        svg {
            width: 1.25rem;
            height: 1.25rem;
            flex-shrink: 0;
        }

        &.full-width {
            flex: 1 1 100%;
            min-width: 100%;
            width: 100%;
            justify-content: center;
        }
    }

    .tag-content {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        text-align: center;
        line-height: 1.15;
        font-family: Poppins, sans-serif;
        flex: 1;
        min-width: 0;

        span {
            font-size: 0.72rem;
            font-weight: 700;
        }

        .tag-sub {
            font-size: 0.65rem;
            font-weight: 500;
            opacity: 0.92;
            margin-top: 0.1rem;
        }
    }
`;

export const CalendarTags = () => {
    return (
        <TagsContainer className="tags-wrapper">
            <Button
                $icon={<LuCalendarCheck />}
                $animationLevel={["constrast", "reduce-scale"]}
                $font="poppins"
                $variant="cyan-filled"
                className="full-width"
                children="Data selecionada"
                style={{ width: "100%" }}
            />
            <Button
                $icon={<LuCalendarCheck />}
                $animationLevel={["constrast", "reduce-scale"]}
                $font="poppins"
                $variant="yellow"
                children={
                    <div className="tag-content">
                        <span>Exclusivo para escolas</span>
                        <span className="tag-sub">09h às 17h</span>
                    </div>
                }
            />
            <Button
                $icon={<LuDoorClosed />}
                $animationLevel={["constrast", "reduce-scale"]}
                $font="poppins"
                $variant="grey"
                children={
                    <div className="tag-content">
                        <span>Parque fechado</span>
                    </div>
                }
            />
            <Button
                $icon={<LuCalendarCheck />}
                $animationLevel={["constrast", "reduce-scale"]}
                $font="poppins"
                $variant="magenta"
                children={
                    <div className="tag-content">
                        <span>Parque aberto</span>
                        <span className="tag-sub">10h às 18h</span>
                    </div>
                }
            />
            <Button
                $icon={<LuCalendarCheck />}
                $animationLevel={["constrast", "reduce-scale"]}
                $font="poppins"
                $variant="green"
                children={
                    <div className="tag-content">
                        <span>Parque aberto</span>
                        <span className="tag-sub">09h às 17h</span>
                    </div>
                }
            />
        </TagsContainer>
    );
};

export const CalendarWithBox = () => {
    return (
        <CalendarWrapper>
            <h2>Escolha a data de visita:</h2>
            <Calendar className={"column"} />

            {/* <CalendarTags /> */}
        </CalendarWrapper>
    );
};
